import React from 'react';
import { Tooltip } from '@mui/material';
import { getConfidenceLevel } from '../utils';

// Set to true to show breakdown tooltips (for dev testing); false for public
const SHOW_CONFIDENCE_BREAKDOWN_TOOLTIPS = false;

/**
 * Confidence Display Component
 * Shows confidence with color-coded indicator and detailed calculation breakdown
 */
export const ConfidenceDisplay = ({ 
    confidence, 
    selected = false, 
    calculationBreakdown = null,
    isLoading = false,
    isMobile = false
}) => {
    // Show subtle placeholder when line movement is still loading
    if (isLoading) {
        const loadingContent = (
            <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: selected ? '#363844' : '',
                    width: '100%',
                    height: '100%',
                    minWidth: isMobile ? 44 : undefined,
                    minHeight: isMobile ? 44 : undefined
                }}>
                    <div 
                        style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            backgroundColor: '#363844',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            opacity: 0.3
                        }}
                    />
                </div>
        );
        return SHOW_CONFIDENCE_BREAKDOWN_TOOLTIPS ? (
            <Tooltip title={isMobile ? 'Loading… Tap and hold for breakdown when ready' : 'Loading line movement data...'} disableTouchListener={false}>
                {loadingContent}
            </Tooltip>
        ) : loadingContent;
    }

    if (confidence === null || confidence === undefined) {
        const nullContent = (
            <span style={{ color: '#aaa', display: 'inline-block', minWidth: isMobile ? 44 : undefined, minHeight: isMobile ? 44 : undefined }}>-</span>
        );
        return SHOW_CONFIDENCE_BREAKDOWN_TOOLTIPS ? (
            <Tooltip title={isMobile ? 'Tap and hold for confidence breakdown when available' : 'No confidence data'} disableTouchListener={false} enterDelay={isMobile ? 300 : 200}>
                {nullContent}
            </Tooltip>
        ) : nullContent;
    }

    const confidenceInfo = getConfidenceLevel(confidence);
    
    // Create detailed tooltip content
    const getTooltipContent = () => {
        if (!calculationBreakdown) {
            return `${confidenceInfo.level} Confidence (${confidence}%) - ${confidenceInfo.description}`;
        }
        
        const { 
            sharpBookCoverageScore, 
            marketAverageValueScore, 
            lineMovementScore, 
            arbitrageProximityScore,
            hasMarketAverage,
            numFactors,
            marketType, 
            sharpBooks, 
            fairValue, 
            betOdds, 
            marketAverage, 
            percentBetterThanMarket,
            sharpDirection, 
            dumbDirection,
            sharpDirectionLabel,
            dumbDirectionLabel
        } = calculationBreakdown;
        
        // Build the calculation summary string dynamically based on available factors
        const factorParts = [sharpBookCoverageScore];
        if (hasMarketAverage) factorParts.push(marketAverageValueScore);
        factorParts.push(lineMovementScore, arbitrageProximityScore);
        const summaryStr = `(${factorParts.join(' + ')}) ÷ ${numFactors || factorParts.length} = ${confidence}`;
        
        return (
            <div style={{ maxWidth: '320px', lineHeight: '1.4' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#ffffff' }}>
                    {confidenceInfo.level} Confidence ({confidence}%)
                </div>
                
                <div style={{ marginBottom: '6px', fontSize: '0.8rem' }}>
                    <strong>{numFactors || 4}-Factor Breakdown (Equal Weight):</strong>
                </div>
                
                {/* Factor 1: Sharp Book Coverage */}
                <div style={{ marginBottom: '6px', fontSize: '0.75rem' }}>
                    <span style={{ color: '#4ade80' }}>1. Sharp Book Coverage:</span> {sharpBookCoverageScore}
                    <br />
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                        {marketType?.toLowerCase().includes('prop') ? 'Player Props' : 'Mainlines'} • {sharpBooks}
                    </span>
                </div>
                
                {/* Factor 2: Market Average Value */}
                <div style={{ marginBottom: '6px', fontSize: '0.75rem' }}>
                    <span style={{ color: hasMarketAverage ? '#4ade80' : '#6b7280' }}>2. Market Average Value:</span>{' '}
                    {hasMarketAverage ? marketAverageValueScore : <span style={{ color: '#6b7280', fontStyle: 'italic' }}>N/A (click play to load)</span>}
                    <br />
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                        {hasMarketAverage ? (
                            <>
                                Bet: {betOdds} • Mkt Avg: {marketAverage}
                                <br />
                                {percentBetterThanMarket} better than market
                            </>
                        ) : (
                            <>Not included in score — no market data loaded</>
                        )}
                    </span>
                </div>
                
                {/* Factor 3: Line Movement Alignment */}
                <div style={{ marginBottom: '6px', fontSize: '0.75rem' }}>
                    <span style={{ color: '#4ade80' }}>3. Line Movement:</span> {lineMovementScore}
                    <br />
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                        Sharp: {sharpDirectionLabel || getMovementText(sharpDirection)}
                        <br />
                        Rec Book: {dumbDirectionLabel || getMovementText(dumbDirection)}
                    </span>
                </div>
                
                {/* Factor 4: Arbitrage Proximity */}
                <div style={{ marginBottom: '6px', fontSize: '0.75rem' }}>
                    <span style={{ color: '#4ade80' }}>4. Arb Proximity:</span> {arbitrageProximityScore}
                    <br />
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                        Bet: {betOdds} vs Sharp (opposite): {fairValue}
                    </span>
                </div>
                
                {/* Calculation Summary */}
                <div style={{ marginTop: '8px', fontSize: '0.7rem', color: '#9ca3af', borderTop: '1px solid #374151', paddingTop: '6px' }}>
                    <span style={{ color: '#60a5fa' }}>
                        {summaryStr}
                    </span>
                </div>
            </div>
        );
    };
    
    const mainContent = (
        <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: selected ? '#363844' : '',
            width: '100%',
            height: '100%',
            minWidth: isMobile ? 44 : undefined,
            minHeight: isMobile ? 44 : undefined,
            cursor: 'pointer'
        }}>
            {/* Confidence indicator - clean circle with confidence number */}
            <div 
                style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: confidenceInfo.color,
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#ffffff'
                }}
            >
                {confidence}
            </div>
        </div>
    );
    return SHOW_CONFIDENCE_BREAKDOWN_TOOLTIPS ? (
        <Tooltip 
            title={getTooltipContent()}
            disableTouchListener={false}
            enterDelay={isMobile ? 400 : 200}
            componentsProps={{
                tooltip: {
                    sx: { 
                        fontSize: '0.875rem',
                        backgroundColor: '#1a1d23',
                        border: '1px solid #363844',
                        maxWidth: '350px'
                    },
                },
            }}
        >
            {mainContent}
        </Tooltip>
    ) : mainContent;
};

// Helper function to convert movement direction to text
function getMovementText(direction) {
    switch (direction) {
        case 1: return '↑ Up';
        case -1: return '↓ Down';
        case 0: return '— Neutral';
        default: return '— Neutral';
    }
}